import React, { useEffect, useRef, useState } from 'react';
import {
    Avatar,
    Box,
    Chip,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import {
    Call as CallIcon,
    CallEnd as CallEndIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Pause as PauseIcon,
    PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useSessionCall } from 'react-sipjs';
import { SessionState } from 'sip.js';

interface CallSessionItemProps {
    sessionId: string;
    onHangup: () => void;
}

const CallSessionItem: React.FC<CallSessionItemProps> = ({ sessionId, onHangup }) => {
    const sessionData = useSessionCall(sessionId);
    const [callDuration, setCallDuration] = useState(0);
    const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (sessionData?.session?.state === SessionState.Established) {
            const startTime = sessionData.timer.answeredAt || sessionData.timer.createdAt;
            durationIntervalRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
                setCallDuration(elapsed);
            }, 1000);
        } else if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }

        return () => {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
            }
        };
    }, [sessionData?.session?.state, sessionData?.timer]);

    if (!sessionData) {
        return null;
    }

    const { session, isHeld, isMuted, decline, hangup, hold, mute, answer, unhold, unmute, direction } = sessionData;

    const handleHangup = () => {
        hangup?.();
        onHangup();
    };

    const formatDuration = (seconds: number): string => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hrs > 0) {
            return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
                Session State: {session?.state || 'Unknown'}
            </Typography>

            <Chip
                label={direction === 'INCOMING' ? 'Incoming Call' : 'Outgoing Call'}
                color={direction === 'INCOMING' ? 'warning' : 'primary'}
                size="small"
                sx={{ mb: 2 }}
            />

            {session?.state === SessionState.Initial && direction === 'INCOMING' && (
                <>
                    <Typography variant="h6" color="warning.main" mb={2}>
                        Incoming Call...
                    </Typography>
                    <Stack direction="row" spacing={3} justifyContent="center" mt={3}>
                        <IconButton
                            onClick={answer}
                            sx={{
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                width: 60,
                                height: 60,
                                '&:hover': {
                                    backgroundColor: '#45a049',
                                },
                            }}
                        >
                            <CallIcon />
                        </IconButton>
                        <IconButton
                            onClick={decline}
                            sx={{
                                backgroundColor: '#F44336',
                                color: 'white',
                                width: 60,
                                height: 60,
                                '&:hover': {
                                    backgroundColor: '#da190b',
                                },
                            }}
                        >
                            <CallEndIcon />
                        </IconButton>
                    </Stack>
                </>
            )}

            {session?.state === SessionState.Establishing && (
                <>
                    <Typography variant="h6" color="primary" mb={2}>
                        {direction === 'OUTGOING' ? 'Calling...' : 'Connecting...'}
                    </Typography>
                    <IconButton
                        onClick={handleHangup}
                        sx={{
                            backgroundColor: '#F44336',
                            color: 'white',
                            width: 60,
                            height: 60,
                            mt: 2,
                            '&:hover': {
                                backgroundColor: '#da190b',
                            },
                        }}
                    >
                        <CallEndIcon />
                    </IconButton>
                </>
            )}

            {session?.state === SessionState.Established && (
                <>
                    <Typography variant="h6" color="success.main" mb={1}>
                        Call Active
                    </Typography>
                    <Typography variant="h5" color="primary" mb={2}>
                        {formatDuration(callDuration)}
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
                        <IconButton
                            onClick={isMuted ? unmute : mute}
                            sx={{
                                backgroundColor: isMuted ? '#F44336' : '#42a5f5',
                                color: 'white',
                                width: 50,
                                height: 50,
                                '&:hover': {
                                    backgroundColor: isMuted ? '#da190b' : '#1976d2',
                                },
                            }}
                        >
                            {isMuted ? <MicOffIcon /> : <MicIcon />}
                        </IconButton>
                        <IconButton
                            onClick={isHeld ? unhold : hold}
                            sx={{
                                backgroundColor: isHeld ? '#FF9800' : '#42a5f5',
                                color: 'white',
                                width: 50,
                                height: 50,
                                '&:hover': {
                                    backgroundColor: isHeld ? '#f57c00' : '#1976d2',
                                },
                            }}
                        >
                            {isHeld ? <PlayArrowIcon /> : <PauseIcon />}
                        </IconButton>
                        <IconButton
                            onClick={handleHangup}
                            sx={{
                                backgroundColor: '#F44336',
                                color: 'white',
                                width: 50,
                                height: 50,
                                '&:hover': {
                                    backgroundColor: '#da190b',
                                },
                            }}
                        >
                            <CallEndIcon />
                        </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
                        {isMuted && <Chip label="Muted" size="small" color="error" />}
                        {isHeld && <Chip label="On Hold" size="small" color="warning" />}
                    </Stack>
                </>
            )}

            {(session?.state === SessionState.Terminating || session?.state === SessionState.Terminated) && (
                <Typography variant="h6" color="text.secondary" mb={2}>
                    Call Ended
                </Typography>
            )}

            {![SessionState.Terminating, SessionState.Terminated].includes(session?.state as SessionState) &&
                session?.state !== SessionState.Initial &&
                session?.state !== SessionState.Establishing && (
                    <IconButton
                        onClick={handleHangup}
                        sx={{
                            backgroundColor: '#F44336',
                            color: 'white',
                            width: 50,
                            height: 50,
                            mt: 2,
                            '&:hover': {
                                backgroundColor: '#da190b',
                            },
                        }}
                    >
                        <CallEndIcon />
                    </IconButton>
                )}
        </Box>
    );
};

export default CallSessionItem;
